import { useMemo, useState } from 'react';
import { toDisplayString } from '@wms/shared';

export type SortDir = 'asc' | 'desc';

/**
 * Pure comparator for table cells: numbers numerically, everything else
 * locale-aware (Lithuanian), with null / undefined / empty sorted last
 * regardless of direction. (QA #6 / WMS-7.)
 */
export function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return toDisplayString(a).localeCompare(toDisplayString(b), 'lt');
}

/** Pure: return a new array sorted by `accessor` in the given direction. */
export function sortRows<T>(rows: readonly T[], accessor: (r: T) => unknown, dir: SortDir): T[] {
  return [...rows].sort((x, y) => {
    const xValue = accessor(x);
    const yValue = accessor(y);
    const hasEmptyValue =
      xValue === null ||
      xValue === undefined ||
      xValue === '' ||
      yValue === null ||
      yValue === undefined ||
      yValue === '';
    const result = compareValues(xValue, yValue);
    return dir === 'desc' && !hasEmptyValue ? -result : result;
  });
}

/**
 * Sortable-table state: click a column to sort by it; click again to flip
 * direction. `accessors` must be a stable reference (define at module scope).
 */
export function useTableSort<T>(
  rows: readonly T[],
  accessors: Record<string, (r: T) => unknown>,
  initial: { key: string; dir?: SortDir },
) {
  const [key, setKey] = useState(initial.key);
  const [dir, setDir] = useState<SortDir>(initial.dir ?? 'asc');
  const sorted = useMemo(() => {
    const acc = accessors[key];
    return acc ? sortRows(rows, acc, dir) : [...rows];
  }, [rows, key, dir, accessors]);
  const toggle = (k: string) => {
    if (k === key) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setKey(k);
      setDir('asc');
    }
  };
  return { sorted, sortKey: key, sortDir: dir, toggle };
}
